// Per-quote-line-item AI-suggestion strip. Mirrors InspectionSuggestionCard.
// Renders ONLY when `suggestion` differs from `currentText`. Click anywhere
// (or the dedicated paste icon) → fires onPaste; the parent appends the
// suggestion to the line item's description and clears description_autofill.
// "x" dismisses for this session only (no PATCH).
//
// v1: no suggestion-event POST (the inspection card has BAR audit logging
// via /items/{id}/suggestion-event — wire later if needed for quote items).

import { useState } from 'react';
import { Box, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconSparkles, IconX } from '@tabler/icons-react';

const SUGGEST_BG = 'rgba(168, 85, 247, 0.06)';
const SUGGEST_BORDER = 'rgba(168, 85, 247, 0.28)';
const SUGGEST_FG = '#A855F7';
const TEXT_SECONDARY = '#A1A1AA';

type Props = {
  currentText: string;
  suggestion: string;
  onPaste: () => void;
  readOnly?: boolean;
};

export function QuoteSuggestionCard({ currentText, suggestion, onPaste, readOnly }: Props) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;
  if (!suggestion) return null;
  if (suggestion.trim() === currentText.trim()) return null;

  const handlePaste = () => {
    onPaste();
    // Close the card right after paste — without this it lingers until the
    // SWR cache round-trips and the dedup (suggestion vs currentText) catches.
    setDismissed(true);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissed(true);
  };

  return (
    <Box
      mb={6}
      onClick={readOnly ? undefined : handlePaste}
      style={{
        background: SUGGEST_BG,
        border: `1px solid ${SUGGEST_BORDER}`,
        borderRadius: 6,
        padding: '5px 8px 5px 9px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 7,
        cursor: readOnly ? 'default' : 'pointer',
      }}
      role={readOnly ? undefined : 'button'}
      aria-label={readOnly ? undefined : 'Paste AI suggestion'}
    >
      <Tooltip label="Click to paste" position="top" withArrow disabled={readOnly}>
        <IconSparkles size={12} color={SUGGEST_FG} style={{ marginTop: 2, flexShrink: 0 }} />
      </Tooltip>
      <Text size="xs" c={TEXT_SECONDARY} style={{ flex: 1, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
        {suggestion}
      </Text>
      <UnstyledButton
        onClick={handleDismiss}
        aria-label="Dismiss suggestion"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: TEXT_SECONDARY,
          padding: 2,
          marginTop: 1,
          flexShrink: 0,
        }}
      >
        <IconX size={11} />
      </UnstyledButton>
    </Box>
  );
}
